<template>
  <div class="liked-posts container mx-auto px-4 py-8">
    <h1 class="text-3xl font-bold mb-8 text-gray-800 dark:text-gray-200">Posts You've Liked</h1>
    
    <div v-if="loading" class="flex justify-center items-center py-12">
      <div class="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
    </div>
    
    <div v-else-if="error" class="bg-red-100 dark:bg-red-900 text-red-700 dark:text-red-200 p-4 rounded-lg mb-6">
      {{ error }}
    </div>
    
    <div v-else-if="likedPosts.length === 0" class="text-center py-12">
      <svg xmlns="http://www.w3.org/2000/svg" class="h-16 w-16 mx-auto text-gray-400 dark:text-gray-600 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
      </svg>
      <h2 class="text-xl font-semibold text-gray-700 dark:text-gray-300 mb-2">No liked posts yet</h2>
      <p class="text-gray-600 dark:text-gray-400 mb-6">When you like posts, they'll appear here for easy access.</p>
      <router-link to="/" class="inline-block bg-blue-500 hover:bg-blue-600 text-white font-medium py-2 px-4 rounded-lg transition-colors duration-200">
        Explore Posts
      </router-link>
    </div>
    
    <div v-else class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
      <BlogCard v-for="post in likedPosts" :key="post.id" :post="post" />
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted, watch } from 'vue'
import { useStore } from 'vuex'
import BlogCard from '@/components/BlogCard.vue'

export default {
  name: 'LikedPosts',
  components: {
    BlogCard
  },
  setup() {
    const store = useStore()
    const loading = ref(true)
    const error = ref(null)
    const likedPosts = ref([])
    
    const isAuthenticated = computed(() => store.getters.isAuthenticated)
    
    const fetchLikedPosts = async () => {
      if (!isAuthenticated.value) {
        error.value = 'You need to be logged in to view your liked posts'
        loading.value = false
        return
      }
      
      try {
        loading.value = true
        error.value = null
        
        // Get user's liked post IDs
        const likedPostIds = await store.dispatch('fetchUserLikes')
        
        // Fetch the actual post data for each liked post
        const posts = []
        for (const postId of likedPostIds) {
          try {
            const post = await store.dispatch('fetchPost', postId)
            if (post) {
              posts.push(post)
            }
          } catch (err) {
            console.error(`Error fetching post ${postId}:`, err)
            // Continue with other posts even if one fails
          }
        }
        
        likedPosts.value = posts
      } catch (err) {
        console.error('Error fetching liked posts:', err)
        error.value = 'Failed to load liked posts. Please try again later.'
      } finally {
        loading.value = false
      }
    }
    
    onMounted(fetchLikedPosts)
    
    // Watch for changes in userLikes and userBookmarks
    const userLikes = computed(() => store.state.userLikes)
    const userBookmarks = computed(() => store.state.userBookmarks)
    
    // Update post like status when userLikes changes
    watch(userLikes, (newLikes) => {
      // If a post is unliked, we need to remove it from the list
      if (likedPosts.value.length > 0) {
        likedPosts.value = likedPosts.value.filter(post => 
          newLikes.includes(post.id)
        )
        
        // Update isLiked flag for remaining posts
        likedPosts.value.forEach(post => {
          post.isLiked = true // All posts in this view should be liked
        })
      }
    })
    
    // Update post bookmark status when userBookmarks changes
    watch(userBookmarks, (newBookmarks) => {
      likedPosts.value.forEach(post => {
        post.isBookmarked = newBookmarks.includes(post.id)
      })
    })
    
    return {
      loading,
      error,
      likedPosts,
      isAuthenticated
    }
  }
}
</script>
